import { fetchCurrentUser, currentUserShowSuccess } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { getWhopPayoutTotal } from '../../util/api';

export const SAVE_MANUAL_PAYOUT_REQUEST = 'app/ManualPayoutPage/SAVE_MANUAL_PAYOUT_REQUEST';
export const SAVE_MANUAL_PAYOUT_SUCCESS = 'app/ManualPayoutPage/SAVE_MANUAL_PAYOUT_SUCCESS';
export const SAVE_MANUAL_PAYOUT_ERROR = 'app/ManualPayoutPage/SAVE_MANUAL_PAYOUT_ERROR';
export const FETCH_PAYOUT_TOTAL_REQUEST = 'app/ManualPayoutPage/FETCH_PAYOUT_TOTAL_REQUEST';
export const FETCH_PAYOUT_TOTAL_SUCCESS = 'app/ManualPayoutPage/FETCH_PAYOUT_TOTAL_SUCCESS';
export const FETCH_PAYOUT_TOTAL_ERROR = 'app/ManualPayoutPage/FETCH_PAYOUT_TOTAL_ERROR';

const initialState = {
  saveInProgress: false,
  saveError: null,
  saved: false,
  totalPaidOutUsd: null,
  fetchTotalInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_MANUAL_PAYOUT_REQUEST:
      return { ...state, saveInProgress: true, saveError: null, saved: false };
    case SAVE_MANUAL_PAYOUT_SUCCESS:
      return { ...state, saveInProgress: false, saved: true };
    case SAVE_MANUAL_PAYOUT_ERROR:
      return { ...state, saveInProgress: false, saveError: payload };
    case FETCH_PAYOUT_TOTAL_REQUEST:
      return { ...state, fetchTotalInProgress: true };
    case FETCH_PAYOUT_TOTAL_SUCCESS:
      return { ...state, fetchTotalInProgress: false, totalPaidOutUsd: payload };
    case FETCH_PAYOUT_TOTAL_ERROR:
      return { ...state, fetchTotalInProgress: false };
    default:
      return state;
  }
}

const saveManualPayoutRequest = () => ({ type: SAVE_MANUAL_PAYOUT_REQUEST });
const saveManualPayoutSuccess = () => ({ type: SAVE_MANUAL_PAYOUT_SUCCESS });
const saveManualPayoutError = e => ({
  type: SAVE_MANUAL_PAYOUT_ERROR,
  error: true,
  payload: e,
});

// Mirrors ManualPayoutPage.js's own maskLast4 — the form only ever displays a
// bank number masked to its last 4 digits, so an unedited field arrives here as
// that mask, not the real number.
const maskLast4 = value => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.length > 4 ? `****${raw.slice(-4)}` : '****';
};

// The bank fields are masked in the UI, so what the form submits is either the
// untouched mask (keep the real stored value) or a freshly typed full number
// (use it as-is).
const resolveBankValue = (submittedValue, storedValue) => {
  const submitted = (submittedValue || '').trim();
  const stored = (storedValue || '').trim();
  return submitted === maskLast4(stored) ? stored : submitted;
};

const fetchPayoutTotalRequest = () => ({ type: FETCH_PAYOUT_TOTAL_REQUEST });
const fetchPayoutTotalSuccess = total => ({ type: FETCH_PAYOUT_TOTAL_SUCCESS, payload: total });
const fetchPayoutTotalError = () => ({ type: FETCH_PAYOUT_TOTAL_ERROR });

export const fetchPayoutTotal = () => dispatch => {
  dispatch(fetchPayoutTotalRequest());
  return getWhopPayoutTotal()
    .then(response => {
      const total = Number(response && response.totalPaidOutUsd) || 0;
      dispatch(fetchPayoutTotalSuccess(total));
      return total;
    })
    .catch(() => {
      // Non-fatal: the tax gate just stays hidden if we can't compute the total.
      dispatch(fetchPayoutTotalError());
    });
};

export const saveManualPayoutDetails = values => (dispatch, getState, sdk) => {
  dispatch(saveManualPayoutRequest());
  const currentUser = getState().user.currentUser;
  const currentPrivateData = currentUser?.attributes?.profile?.privateData || {};
  const existingApproval = currentPrivateData.testerApprovalStatus;
  const existingDetails = currentPrivateData.manualPayoutDetails || {};

  const addressStreet = (values.addressStreet || '').trim();
  const addressCity = (values.addressCity || '').trim();
  const addressState = (values.addressState || '').trim();
  const addressZip = (values.addressZip || '').trim();
  // The form only ever shows these masked, so resolve back to the real stored
  // value unless the tester actually typed a new one.
  const bankRoutingNumber = resolveBankValue(
    values.bankRoutingNumber,
    existingDetails.bankRoutingNumber
  );
  const bankAccountNumber = resolveBankValue(
    values.bankAccountNumber,
    existingDetails.bankAccountNumber
  );
  // Once an admin archives the bank details (masking them to ****stubs), that
  // archived marker only survives while the tester leaves address + bank numbers
  // untouched. Changing any of them makes the details "new" again, so they
  // reappear as pending in the admin banking tab for the admin to re-save.
  const bankDetailsUnchanged =
    addressStreet === (existingDetails.addressStreet || '') &&
    addressCity === (existingDetails.addressCity || '') &&
    addressState === (existingDetails.addressState || '') &&
    addressZip === (existingDetails.addressZip || '') &&
    bankRoutingNumber === (existingDetails.bankRoutingNumber || '') &&
    bankAccountNumber === (existingDetails.bankAccountNumber || '');

  const privateData = {
    ...currentPrivateData,
    manualPayoutDetails: {
      legalName: values.legalName,
      payoutMethod: values.payoutMethod,
      payoutPhone: values.payoutPhone,
      payoutHandle: values.payoutHandle,
      country: values.country,
      addressStreet,
      addressCity,
      addressState,
      addressZip,
      bankRoutingNumber,
      bankAccountNumber,
      bankDetailsArchivedAt: bankDetailsUnchanged
        ? existingDetails.bankDetailsArchivedAt || null
        : null,
      // The admin's copy progress (see server copyBanking) survives a re-save as
      // long as the bank details themselves didn't change; new numbers need to
      // be copied from scratch.
      bankInfoCopyCount: bankDetailsUnchanged ? existingDetails.bankInfoCopyCount || 0 : 0,
      backupPayoutMethod: values.backupPayoutMethod || null,
      backupPayoutDetails: values.backupPayoutDetails || null,
      status: 'complete',
      submittedAt: new Date().toISOString(),
    },
    testerApprovalStatus: existingApproval === 'approved' ? 'needs_review' : 'pending',
  };

  return sdk.currentUser
    .updateProfile({ privateData }, { expand: true })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      const user = entities[0];
      dispatch(currentUserShowSuccess(user));
      dispatch(saveManualPayoutSuccess());
      return user;
    })
    .catch(e => {
      dispatch(saveManualPayoutError(storableError(e)));
      throw e;
    });
};

export const loadData = () => dispatch => {
  dispatch(fetchCurrentUser());
  return dispatch(fetchPayoutTotal());
};
